/**
 * <Tooltip> — Non-interactive supplementary info on hover or keyboard focus.
 *
 * Built on @radix-ui/react-tooltip. Use for:
 *   - Icon button labels ("Edit", "Delete", "Settings")
 *   - Truncated text overflow ("Read more on hover")
 *   - Feature discovery ("This is how you export data")
 *
 * Constraints:
 *   - Tooltip content MUST NOT be interactive. If interaction needed, use <Popover>.
 *   - Tooltip appears after a 300ms hover delay (configurable via TooltipProvider).
 *   - Hides immediately on mouse-leave or blur.
 *
 * Two API styles:
 *
 * 1. Composition (full control):
 *
 *    <Tooltip.Root>
 *      <Tooltip.Trigger asChild>
 *        <button aria-label="Edit"><EditIcon /></button>
 *      </Tooltip.Trigger>
 *      <Tooltip.Content side="top">
 *        Edit this field
 *        <Tooltip.Arrow />
 *      </Tooltip.Content>
 *    </Tooltip.Root>
 *
 * 2. Shorthand (common case):
 *
 *    <Tooltip label="Edit">
 *      <button aria-label="Edit"><EditIcon /></button>
 *    </Tooltip>
 *
 * Both require <TooltipProvider> at the app root (already provided via
 * <OverlayProvider> in main.jsx).
 *
 * See: docs/architecture/OVERLAY_ARCHITECTURE.md §4.5
 */

import React from 'react';
import * as RadixTooltip from '@radix-ui/react-tooltip';
import { useOverlayPortal } from './useOverlayPortal';

/**
 * TooltipRoot — wraps Radix Tooltip.Root with sensible defaults.
 */
function TooltipRoot({ open, defaultOpen, onOpenChange, delayDuration, children }) {
  return (
    <RadixTooltip.Root
      open={open}
      defaultOpen={defaultOpen}
      onOpenChange={onOpenChange}
      delayDuration={delayDuration}
    >
      {children}
    </RadixTooltip.Root>
  );
}
TooltipRoot.displayName = 'Tooltip.Root';

const TooltipTrigger = RadixTooltip.Trigger;

/**
 * TooltipContent — floating tooltip body.
 *
 * @param {object} props
 * @param {React.ReactNode} props.children
 * @param {'top'|'right'|'bottom'|'left'} [props.side='top']
 * @param {'start'|'center'|'end'} [props.align='center']
 * @param {number} [props.sideOffset=4]
 * @param {number} [props.collisionPadding=8]
 * @param {string} [props.className]
 */
function TooltipContent({
  children,
  side = 'top',
  align = 'center',
  sideOffset = 4,
  collisionPadding = 8,
  className = '',
  ...rest
}) {
  const portalContainer = useOverlayPortal();

  return (
    <RadixTooltip.Portal container={portalContainer}>
      <RadixTooltip.Content
        side={side}
        align={align}
        sideOffset={sideOffset}
        collisionPadding={collisionPadding}
        className={`max-w-[280px] px-2.5 py-1.5 text-xs font-medium leading-snug bg-[var(--aif-bg-elevated)] text-[var(--aif-text-primary)] border border-[var(--aif-border-default)] rounded-[var(--aif-radius-sm)] shadow-[var(--aif-shadow-md)] data-[state=delayed-open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=delayed-open]:fade-in-0 data-[state=closed]:zoom-out-95 data-[state=delayed-open]:zoom-in-95 ${className}`}
        style={{ zIndex: 'var(--aif-z-contextual)' }}
        {...rest}
      >
        {children}
      </RadixTooltip.Content>
    </RadixTooltip.Portal>
  );
}
TooltipContent.displayName = 'Tooltip.Content';

/**
 * TooltipArrow — small triangle pointing to trigger. Optional.
 */
function TooltipArrow({ className = '', ...rest }) {
  return (
    <RadixTooltip.Arrow
      className={`fill-[var(--aif-bg-elevated)] stroke-[var(--aif-border-default)] ${className}`}
      width={10}
      height={5}
      {...rest}
    />
  );
}
TooltipArrow.displayName = 'Tooltip.Arrow';

/**
 * Shorthand Tooltip — wraps common case where you just want a label on hover.
 *
 * @param {object} props
 * @param {string|React.ReactNode} props.label - Tooltip text content
 * @param {React.ReactElement} props.children - The trigger element (single child)
 * @param {'top'|'right'|'bottom'|'left'} [props.side='top']
 * @param {number} [props.delayDuration] - Override default 300ms delay
 * @param {boolean} [props.showArrow=false]
 */
export function Tooltip({ label, children, side = 'top', delayDuration, showArrow = false, ...rest }) {
  if (!label) {
    // No label → render children without tooltip wrapper
    return children;
  }

  return (
    <TooltipRoot delayDuration={delayDuration}>
      <TooltipTrigger asChild>{children}</TooltipTrigger>
      <TooltipContent side={side} {...rest}>
        {label}
        {showArrow && <TooltipArrow />}
      </TooltipContent>
    </TooltipRoot>
  );
}
Tooltip.displayName = 'Tooltip';

// Composite API for advanced cases
Tooltip.Root = TooltipRoot;
Tooltip.Trigger = TooltipTrigger;
Tooltip.Content = TooltipContent;
Tooltip.Arrow = TooltipArrow;
